import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  Calendar,
  Clock,
  User,
  ChevronDown,
  PackageOpen,
  ArrowUpDown,
  Trophy,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Briefcase,
} from 'lucide-react';
import { Pagination } from 'antd';
import { authFetch } from '../services/auth';

type SortField = 'time' | 'score';
type SortOrder = 'asc' | 'desc';
type TabType = 'interview' | 'salary';

interface InterviewRecord {
  id: number;
  job_type: string;
  job_level: string;
  interview_type: string;
  interviewer_id: string;
  score: number;
  created_at: number;
  messages?: any[];
  report?: any;
}

interface SalaryNegotiationRecord {
  id: number;
  jobType: string;
  jobLevel: string;
  finalOffer: number;
  isAccepted: number;
  totalRounds: number;
  score: number;
  duration: number;
  createdAt: number;
}

// 根据评分获取颜色和等级信息 - 与简历评估结果页保持一致
const getScoreInfo = (score: number) => {
  if (score >= 90) {
    return {
      color: '#22c55e',
      gradient: 'from-green-400 via-green-500 to-green-600',
      label: '卓越',
      glow: 'shadow-green-500/30',
      textColor: 'text-green-400',
    };
  }
  if (score >= 80) {
    return {
      color: '#f59e0b',
      gradient: 'from-amber-400 via-amber-500 to-amber-600',
      label: '优秀',
      glow: 'shadow-amber-500/30',
      textColor: 'text-amber-400',
    };
  }
  if (score >= 70) {
    return {
      color: '#eab308',
      gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
      label: '良好',
      glow: 'shadow-yellow-500/30',
      textColor: 'text-yellow-400',
    };
  }
  if (score >= 60) {
    return {
      color: '#f97316',
      gradient: 'from-orange-400 via-orange-500 to-orange-600',
      label: '及格',
      glow: 'shadow-orange-500/30',
      textColor: 'text-orange-400',
    };
  }
  return {
    color: '#ef4444',
    gradient: 'from-red-400 via-red-500 to-red-600',
    label: '待提升',
    glow: 'shadow-red-500/30',
    textColor: 'text-red-400',
  };
};

// 评分圆环组件 - 增强版
const ScoreRing = ({
  score,
  size = 64,
  strokeWidth = 4,
}: {
  score: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const scoreInfo = getScoreInfo(score);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* 外发光效果 */}
      <div
        className={`absolute inset-0 rounded-full blur-md opacity-50 bg-gradient-to-br ${scoreInfo.gradient}`}
      />

      <svg
        className="w-full h-full transform -rotate-90 relative z-10"
        style={{ width: size, height: size }}
      >
        {/* 背景圆环 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#1E293B"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* 进度圆环 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={scoreInfo.color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>

      {/* 中心内容 */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
        <span className={`text-lg font-bold leading-none ${scoreInfo.textColor}`}>
          {Math.ceil(score)}
        </span>
      </div>
    </div>
  );
};

const ITEMS_PER_PAGE = 20;

export default function HistoryPage() {
  const navigate = useNavigate();

  // Tab状态
  const [activeTab, setActiveTab] = useState<TabType>('interview');

  // 面试历史数据
  const [records, setRecords] = useState<InterviewRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);

  // 谈薪历史数据
  const [salaryRecords, setSalaryRecords] = useState<SalaryNegotiationRecord[]>(
    [],
  );
  const [salaryLoading, setSalaryLoading] = useState(true);
  const [salaryError, setSalaryError] = useState('');
  const salaryHasFetched = useRef(false);

  // 从 Redux 获取面试官列表
  const configData = useSelector((state: RootState) => state.config.data);
  const interviewers = configData?.interviewers || [];

  // 排序状态
  const [sortField, setSortField] = useState<SortField>('time');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [salaryCurrentPage, setSalaryCurrentPage] = useState(1);
  const [salaryTotalRecords, setSalaryTotalRecords] = useState(0);

  useEffect(() => {
    // 防止 React 18 StrictMode 导致的重复请求
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (salaryHasFetched.current) return;
    salaryHasFetched.current = true;
    fetchSalaryHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = async (
    page = currentPage,
    field = sortField,
    order = sortOrder,
  ) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: ITEMS_PER_PAGE.toString(),
        sortField: field === 'time' ? 'created_at' : 'score',
        sortOrder: order,
      });

      const response = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/interviews?${params}`,
      );

      if (!response.ok) {
        throw new Error('获取面试历史失败');
      }

      const data = await response.json();
      setRecords(data.interviews || []);
      // 更新总记录数
      if (data.pagination) {
        setTotalRecords(data.pagination.total);
      }
    } catch (err) {
      setError('获取面试历史失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryHistory = async (
    page = salaryCurrentPage,
    field = sortField,
    order = sortOrder,
  ) => {
    setSalaryLoading(true);
    setSalaryError('');

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: ITEMS_PER_PAGE.toString(),
        sortField: field === 'time' ? 'created_at' : 'score',
        sortOrder: order,
      });

      const response = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/salary-negotiations?${params}`,
      );

      if (!response.ok) {
        throw new Error('获取谈薪历史失败');
      }

      const data = await response.json();
      setSalaryRecords(data.records || []);
      // 更新总记录数
      if (data.pagination) {
        setSalaryTotalRecords(data.pagination.total);
      }
    } catch (err) {
      setSalaryError('获取谈薪历史失败，请稍后重试');
    } finally {
      setSalaryLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}分${secs}秒`;
  };

  const getInterviewerName = (interviewerId: string) => {
    const interviewer = interviewers.find((i) => i.id === interviewerId);
    return interviewer?.name || '未知面试官';
  };

  const getInterviewerAvatar = (interviewerId: string) => {
    const interviewer = interviewers.find((i) => i.id === interviewerId);
    return interviewer?.avatar;
  };

  // 处理排序
  const handleSort = (field: SortField) => {
    const newOrder =
      sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
    if (sortField === field) {
      setSortOrder(newOrder);
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
    setSalaryCurrentPage(1);
    setShowSortDropdown(false);
    // 重新获取数据
    if (activeTab === 'interview') {
      fetchHistory(1, field, sortField === field ? newOrder : 'desc');
    } else {
      fetchSalaryHistory(1, field, sortField === field ? newOrder : 'desc');
    }
  };

  // 获取排序按钮文本
  const getSortLabel = () => {
    if (sortField === 'time') {
      return sortOrder === 'desc' ? '时间倒序' : '时间正序';
    } else {
      return sortOrder === 'desc' ? '得分倒序' : '得分正序';
    }
  };

  // 点击面试记录跳转到报告页，URL 上拼接 id
  const handleRecordClick = (record: InterviewRecord) => {
    navigate(`/report/${record.id}`);
  };

  // 当前Tab的数据
  const isLoading = activeTab === 'interview' ? loading : salaryLoading;
  const hasError = activeTab === 'interview' ? error : salaryError;
  const currentRecords = activeTab === 'interview' ? records : salaryRecords;
  const currentTotal =
    activeTab === 'interview' ? totalRecords : salaryTotalRecords;
  const currentPageNum =
    activeTab === 'interview' ? currentPage : salaryCurrentPage;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B]">
      <div className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* 标题 */}
        <div className="relative mb-8">
          <h1 className="text-2xl font-bold text-white text-center">
            面试历史
          </h1>
        </div>

        {/* Tab切换和统计信息 */}
        <div className="flex items-center justify-between mb-8">
          {/* Tab切换 */}
          <div className="inline-flex bg-slate-800/60 backdrop-blur-sm rounded-xl p-1 border border-slate-700/50">
            <button
              onClick={() => setActiveTab('interview')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                activeTab === 'interview'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              职位面试
            </button>
            <button
              onClick={() => setActiveTab('salary')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                activeTab === 'salary'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              薪酬沟通
            </button>
          </div>

          {/* 统计信息和排序 */}
          <div className="flex items-center gap-4 text-sm">
            {!isLoading && (
              <div className="text-slate-400">
                总次数:{' '}
                <span className="text-white font-semibold">{currentTotal}</span>
              </div>
            )}
            {/* 排序下拉菜单 */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-600/50 rounded-lg text-slate-300 transition-all cursor-pointer"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                <span>{getSortLabel()}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${
                    showSortDropdown ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {showSortDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-[90]"
                    onClick={() => setShowSortDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[100] py-1">
                    <button
                      onClick={() => handleSort('time')}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-700/50 hover:cursor-pointer transition-colors ${
                        sortField === 'time'
                          ? 'text-amber-400'
                          : 'text-slate-300'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5" />
                        按时间
                      </span>
                      {sortField === 'time' &&
                        (sortOrder === 'desc' ? (
                          <ArrowDown className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ))}
                    </button>
                    <button
                      onClick={() => handleSort('score')}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-700/50 hover:cursor-pointer transition-colors ${
                        sortField === 'score'
                          ? 'text-amber-400'
                          : 'text-slate-300'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Trophy className="w-3.5 h-3.5" />
                        按得分
                      </span>
                      {sortField === 'score' &&
                        (sortOrder === 'desc' ? (
                          <ArrowDown className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ))}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/30 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : hasError ? (
            <div className="text-center py-16">
              <button
                onClick={() =>
                  activeTab === 'interview'
                    ? fetchHistory()
                    : fetchSalaryHistory()
                }
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                重新加载
              </button>
            </div>
          ) : currentRecords.length === 0 ? (
            <div className="text-center py-20">
              {/* 空状态 - 放大镜（无数据） */}
              <div className="relative mx-auto mb-6 w-32 h-32">
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 80 80"
                    fill="none"
                    className="text-slate-500"
                  >
                    {/* 放大镜镜片外圈 */}
                    <circle
                      cx="35"
                      cy="35"
                      r="22"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                    {/* 放大镜手柄 */}
                    <line
                      x1="52"
                      y1="52"
                      x2="68"
                      y2="68"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* 镜片内装饰 - 表示空白/无内容 */}
                    <circle
                      cx="35"
                      cy="35"
                      r="16"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                      opacity="0.3"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-slate-400 mb-2 text-lg font-medium">
                暂无{activeTab === 'interview' ? '面试' : '谈薪'}记录
              </p>
              <p className="text-slate-500 text-sm max-w-xs mx-auto">
                快去开始第一次{activeTab === 'interview' ? '面试' : '谈薪模拟'}
                吧，记录将在这里显示
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/30 custom-scrollbar">
              {activeTab === 'interview'
                ? records.map((record, index) => (
                    <div
                      key={record.id}
                      onClick={() => handleRecordClick(record)}
                      className="p-6 hover:bg-white/5 transition-all duration-300 cursor-pointer group"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* 岗位信息 */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center border border-amber-500/30">
                              <img
                                src={getInterviewerAvatar(
                                  record.interviewer_id,
                                )}
                                alt={getInterviewerName(record.interviewer_id)}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">
                                {record.job_type}
                              </h3>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 text-xs">
                                  {record.job_level}
                                </span>
                                <span className="text-slate-500">·</span>
                                <span className="text-slate-400">
                                  {record.interview_type}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* 面试官和时间 */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 ml-[60px]">
                            <div className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5" />
                              <span>
                                {getInterviewerName(record.interviewer_id)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{formatDate(record.created_at)}</span>
                            </div>
                          </div>
                        </div>

                        {/* 评分 */}
                        {record.score !== null && record.score !== undefined ? (
                          <div className="flex items-center gap-3 ml-4">
                            <ScoreRing score={record.score} />
                            <div className="text-right hidden sm:block">
                              <div className="text-xs text-slate-500">
                                综合评分
                              </div>
                              <div
                                className={`text-sm font-bold bg-gradient-to-r ${
                                  getScoreInfo(record.score).gradient
                                } bg-clip-text text-transparent`}
                              >
                                {getScoreInfo(record.score).label}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 ml-4 text-slate-500">
                            <Clock className="w-5 h-5" />
                            <span className="text-sm">评分中</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                : salaryRecords.map((record, index) => (
                    <div
                      key={record.id}
                      onClick={() => navigate(`/salary-report/${record.id}`)}
                      className="p-6 hover:bg-white/5 transition-all duration-300 cursor-pointer group"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* 岗位信息 */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center border border-amber-500/30">
                              <img
                                src="https://admin-bucket.mmbang.site/pics/HR.png"
                                alt="HR总监"
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">
                                {record.jobType}
                              </h3>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 text-xs">
                                  {record.jobLevel}
                                </span>
                                <span className="text-slate-500">·</span>
                                <span
                                  className={
                                    record.isAccepted
                                      ? 'text-emerald-400'
                                      : 'text-red-400'
                                  }
                                >
                                  {record.isAccepted
                                    ? `${record.finalOffer}k`
                                    : '未达成'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* 谈薪详情和时间 */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 ml-[60px]">
                            <div className="flex items-center gap-1.5">
                              <RefreshCw className="w-3.5 h-3.5" />
                              <span>{record.totalRounds}轮谈判</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{formatDuration(record.duration)}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{formatDate(record.createdAt)}</span>
                            </div>
                          </div>
                        </div>

                        {/* 评分 */}
                        <div className="flex items-center gap-3 ml-4">
                          <ScoreRing score={record.score} />
                          <div className="text-right hidden sm:block">
                            <div className="text-xs text-slate-500">
                              综合评分
                            </div>
                            <div
                              className={`text-sm font-bold bg-gradient-to-r ${
                                getScoreInfo(record.score).gradient
                              } bg-clip-text text-transparent`}
                            >
                              {getScoreInfo(record.score).label}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          )}
        </div>

        {/* 分页 */}
        {!isLoading && !hasError && currentRecords.length > 0 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              current={currentPageNum}
              total={currentTotal}
              pageSize={ITEMS_PER_PAGE}
              onChange={(page) => {
                if (activeTab === 'interview') {
                  setCurrentPage(page);
                  fetchHistory(page, sortField, sortOrder);
                } else {
                  setSalaryCurrentPage(page);
                  fetchSalaryHistory(page, sortField, sortOrder);
                }
              }}
              showSizeChanger={false}
              className="custom-pagination"
            />
          </div>
        )}
      </div>

      {/* 自定义滚动条样式 */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(71, 85, 105, 0.8);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 1);
        }
        /* antd 分页组件样式覆盖 */
        .custom-pagination .ant-pagination-item {
          background-color: rgba(30, 41, 59, 0.8);
          border-color: rgba(71, 85, 105, 0.5);
        }
        .custom-pagination .ant-pagination-item a {
          color: #94a3b8;
        }
        .custom-pagination .ant-pagination-item:hover {
          background-color: rgba(51, 65, 85, 0.8);
          border-color: rgba(245, 158, 11, 0.5);
        }
        .custom-pagination .ant-pagination-item:hover a {
          color: #e2e8f0;
        }
        .custom-pagination .ant-pagination-item-active {
          background-color: #f59e0b;
          border-color: #f59e0b;
        }
        .custom-pagination .ant-pagination-item-active a {
          color: #ffffff;
        }
        .custom-pagination .ant-pagination-prev button,
        .custom-pagination .ant-pagination-next button {
          background-color: rgba(30, 41, 59, 0.8);
          border-color: rgba(71, 85, 105, 0.5);
          color: #94a3b8;
        }
        .custom-pagination .ant-pagination-prev:hover button,
        .custom-pagination .ant-pagination-next:hover button {
          background-color: rgba(51, 65, 85, 0.8);
          border-color: rgba(245, 158, 11, 0.5);
          color: #e2e8f0;
        }
        .custom-pagination .ant-pagination-disabled button {
          background-color: rgba(30, 41, 59, 0.4);
          border-color: rgba(71, 85, 105, 0.3);
          color: #64748b;
        }
        .custom-pagination .ant-pagination-jump-prev::after,
        .custom-pagination .ant-pagination-jump-next::after {
          color: #94a3b8;
        }
      `}</style>
    </div>
  );
}
